import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type TableName = keyof Database['public']['Tables'];
type TableInsert<T extends TableName> = Database['public']['Tables'][T]['Insert'];
type TableRow<T extends TableName> = Database['public']['Tables'][T]['Row'];

interface StaticAsset {
  id: string;
  url: string;
  type: 'logo' | 'image';
  data: Blob;
}

interface PendingOperation<T extends TableName = TableName> {
  id: string;
  tableName: T;
  type: 'create' | 'update' | 'delete';
  data: TableInsert<T>;
  timestamp: number;
}

interface OfflineDB extends DBSchema {
  pendingOperations: {
    key: string;
    value: PendingOperation;
    indexes: { 'by-timestamp': number };
  };
  staticAssets: {
    key: string;
    value: StaticAsset;
    indexes: { 'by-type': string };
  };
  academic_years: {
    key: string;
    value: TableRow<'academic_years'>;
    indexes: { 'by-school': string };
  };
  class_streams: {
    key: string;
    value: TableRow<'class_streams'>;
    indexes: { 'by-school': string };
  };
  levels: {
    key: string;
    value: TableRow<'levels'>;
    indexes: { 'by-school': string };
  };
  medical_alert_templates: {
    key: string;
    value: TableRow<'medical_alert_templates'>;
    indexes: { 'by-school': string };
  };
  medical_suggestions: {
    key: string;
    value: TableRow<'medical_suggestions'>;
    indexes: { 'by-school': string };
  };
  profiles: {
    key: string;
    value: TableRow<'profiles'>;
    indexes: { 'by-school': string };
  };
  referrals: {
    key: string;
    value: TableRow<'referrals'>;
    indexes: { 'by-school': string };
  };
  reports: {
    key: string;
    value: TableRow<'reports'>;
    indexes: { 'by-school': string };
  };
  school_classes: {
    key: string;
    value: TableRow<'school_classes'>;
    indexes: { 'by-school': string };
  };
  schools: {
    key: string;
    value: TableRow<'schools'>;
  };
  community_members: {
    key: string;
    value: TableRow<'community_members'>;
    indexes: { 'by-school': string };
  };
  streams: {
    key: string;
    value: TableRow<'streams'>;
    indexes: { 'by-school': string };
  };
  students: {
    key: string;
    value: TableRow<'students'>;
    indexes: { 'by-school': string };
  };
  terms: {
    key: string;
    value: TableRow<'terms'>;
    indexes: { 'by-school': string };
  };
  visits: {
    key: string;
    value: TableRow<'visits'>;
    indexes: { 'by-school': string };
  };
}

let db: IDBPDatabase<OfflineDB>;

export const initDB = async () => {
  try {
    db = await openDB<OfflineDB>('healthApp', 5, {
      upgrade(db, oldVersion, newVersion, transaction) {
        // Clear all object stores if they exist
        Array.from(db.objectStoreNames).forEach(storeName => {
          db.deleteObjectStore(storeName);
        });

        // Create pendingOperations store with index
        const pendingOpsStore = db.createObjectStore('pendingOperations', { keyPath: 'id' });
        pendingOpsStore.createIndex('by-timestamp', 'timestamp');

        // Create static assets store
        const staticAssetsStore = db.createObjectStore('staticAssets', { keyPath: 'id' });
        staticAssetsStore.createIndex('by-type', 'type');
        
        // Create stores for all tables
        const tables: TableName[] = [
          'academic_years',
          'class_streams',
          'levels',
          'medical_alert_templates',
          'medical_suggestions',
          'profiles',
          'referrals',
          'reports',
          'school_classes',
          'schools',
          'community_members',
          'streams',
          'students',
          'terms',
          'visits'
        ];
        
        tables.forEach((table: string) => {
          const store = (db as any).createObjectStore(table, { keyPath: 'id' });
          if (table !== 'schools') {
            store.createIndex('by-school', 'school_id');
          }
        });
      },
    });
    
    console.log('IndexedDB initialized successfully');
    return true;
  } catch (error) {
    console.error('Failed to initialize IndexedDB:', error);
    throw error;
  }
};

export const queueOperation = async <T extends TableName>(
  tableName: T,
  type: 'create' | 'update' | 'delete',
  data: TableInsert<T>
): Promise<void> => {
  if (!db) await initDB();

  const operation: PendingOperation<T> = {
    id: crypto.randomUUID(),
    tableName,
    type,
    data,
    timestamp: Date.now()
  };

  await db.put('pendingOperations', operation);
  
  if (type !== 'delete') {
    await cacheTableData(tableName, [data as TableRow<T>]);
  }
  
  console.log(`${type} operation queued for ${tableName}`);
};

export const cacheTableData = async <T extends TableName>(
  tableName: T, 
  data: TableRow<T>[]
): Promise<void> => {
  if (!db) await initDB();

  const tx = (db as any).transaction(tableName, 'readwrite');
  const store = tx.objectStore(tableName);
  
  await Promise.all([
    ...data.map((item: any) => store.put(item)),
    tx.done
  ]);
  
  console.log(`${tableName} data cached successfully`);
};

export const getCachedData = async <T extends TableName>(
  tableName: T,
  schoolId?: string
): Promise<TableRow<T>[]> => {
  if (!db) await initDB();
  
  const tx = (db as any).transaction(tableName, 'readonly');
  
  if (schoolId && tableName !== 'schools') {
    const index = tx.store.index('by-school');
    return index.getAll(schoolId);
  }
  
  return tx.store.getAll();
};

export const searchCachedData = async <T extends TableName>(
  tableName: T,
  searchTerm: string,
  fields: (keyof TableRow<T>)[]
): Promise<TableRow<T>[]> => {
  if (!db) await initDB();
  
  const items = await (db as any).getAll(tableName);
  const loweredTerm = searchTerm.toLowerCase();
  
  return items.filter((item: any) => 
    fields.some(field => {
      const value = item[field as string];
      return value != null && String(value).toLowerCase().includes(loweredTerm);
    })
  );
};

export const syncPendingOperations = async (): Promise<void> => {
  if (!db) await initDB();

  try {
    const pendingOps = await db.getAllFromIndex('pendingOperations', 'by-timestamp');
    
    for (const op of pendingOps) {
      try {
        switch (op.type) {
          case 'create':
            await supabase.from(op.tableName).insert(op.data);
            break;
          case 'update':
            await supabase.from(op.tableName)
              .update(op.data)
              .eq('id', (op.data as any).id);
            break;
          case 'delete':
            await supabase.from(op.tableName)
              .delete()
              .eq('id', (op.data as any).id);
            break;
        }
        
        await db.delete('pendingOperations', op.id);
        console.log(`Synced ${op.type} operation for ${op.tableName}`);
      } catch (error) {
        console.error(`Failed to sync operation ${op.id}:`, error);
      }
    }
  } catch (error) {
    console.error('Error during sync:', error);
    throw error;
  }
};

export const clearCache = async (tableName: TableName): Promise<void> => {
  if (!db) await initDB();
  
  const tx = (db as any).transaction(tableName, 'readwrite');
  await tx.store.clear();
  await tx.done;
  console.log(`${tableName} cache cleared`);
};

export const clearAllCaches = async (): Promise<void> => {
  if (!db) await initDB();

  const tables: TableName[] = [
    'academic_years',
    'class_streams', 
    'levels',
    'medical_alert_templates',
    'medical_suggestions',
    'profiles',
    'referrals',
    'reports',
    'school_classes',
    'schools',
    'community_members',
    'streams', 
    'students',
    'terms',
    'visits'
  ];
  
  await Promise.all(tables.map(clearCache));
  console.log('All caches cleared');
};

export const cacheStaticAsset = async (id: string, url: string, type: StaticAsset['type']) => {
  if (!db) await initDB();

  try {
    // Fetch the image
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch asset');

    const blob = await response.blob();
    const asset: StaticAsset = {
      id,
      url,
      type,
      data: blob
    };

    // Store in IndexedDB
    await db.put('staticAssets', asset);
    console.log(`Asset ${id} cached successfully`);
  } catch (error) {
    console.error('Failed to cache static asset:', error);
    throw error;
  }
};

export const getStaticAsset = async (id: string): Promise<string | null> => {
  if (!db) await initDB();

  try {
    const asset = await db.get('staticAssets', id);
    if (!asset) return null;

    return URL.createObjectURL(asset.data);
  } catch (error) {
    console.error('Failed to retrieve static asset:', error);
    return null;
  }
};
